import html2canvas from 'html2canvas'

function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
}

function collectPageStyleText(): string {
  const chunks: string[] = []

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      chunks.push(Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n'))
    } catch {
      // Cross-origin stylesheet (e.g. Google Fonts) — irrelevant to chart shape
      // styling (fills/strokes come from same-origin CSS Modules), skip it.
    }
  }

  return chunks.join('\n')
}

// Rasterizes an already-self-contained SVG document (fonts/colors baked in,
// no reliance on the live page's stylesheet) at 2x scale onto a white
// background, shared by both export paths below.
async function rasterizeSvgText(svgText: string, width: number, height: number): Promise<string> {
  const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' })
  const objectUrl = URL.createObjectURL(svgBlob)

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Falha ao rasterizar o gráfico.'))
      img.src = objectUrl
    })

    const exportScale = 2
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width * exportScale))
    canvas.height = Math.max(1, Math.round(height * exportScale))

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Contexto 2D do canvas indisponível.')
    }

    context.scale(exportScale, exportScale)
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    return canvas.toDataURL('image/png')
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function svgToPngDataUrl(svg: SVGSVGElement): Promise<string> {
  const bounds = svg.getBoundingClientRect()

  // The chart's colors (e.g. MP vs MF series) come from CSS Module classes,
  // not inline attributes. A bare XMLSerializer dump loses all of that once
  // rendered outside the page (the <img> has no access to the page's
  // stylesheets), so the exported PNG would come out flat black. Embedding
  // the resolved CSS text directly in the SVG keeps it self-contained.
  const clone = svg.cloneNode(true) as SVGSVGElement
  const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style')
  styleEl.textContent = collectPageStyleText()
  clone.insertBefore(styleEl, clone.firstChild)

  return rasterizeSvgText(new XMLSerializer().serializeToString(clone), bounds.width, bounds.height)
}

// Fallback for charts built from plain DOM (e.g. the div/CSS bar stacks in
// Ruptura Digital — no <canvas> or <svg> to grab). Wrapping the element in an
// SVG <foreignObject> and drawing that to a canvas — the same trick as
// svgToPngDataUrl — looks like it should work, but Chromium taints any
// canvas drawn from an SVG containing foreignObject-embedded HTML
// (SecurityError on toDataURL, even with zero cross-origin content). html2canvas
// sidesteps that entirely: it walks the DOM and repaints computed styles onto
// the canvas directly, never routing through an SVG/Image intermediate.
async function domElementToPngDataUrl(element: HTMLElement): Promise<string> {
  // Capturing the live, in-place element sometimes crashes html2canvas
  // ("createPattern... canvas element with a width or height of 0") on
  // blocks with a CSS grid track (e.g. Quantidade MP's bar rows) — traced to
  // constraining the off-screen wrapper to the original's exact pixel width:
  // that forces the same grid math to re-run against a clone, and a
  // fractional width occasionally rounds some column to 0. Leaving the
  // wrapper unconstrained (sized to its own content instead) sidesteps that
  // rounding edge case entirely, and still fixes the original SecurityError
  // this wrapper was introduced for (foreignObject/tainted-canvas — see the
  // svgToPngDataUrl comment above).
  const wrapper = document.createElement('div')
  wrapper.style.position = 'fixed'
  wrapper.style.top = '-99999px'
  wrapper.style.left = '-99999px'
  wrapper.appendChild(element.cloneNode(true))
  document.body.appendChild(wrapper)

  try {
    const canvas = await html2canvas(wrapper, { scale: 2, backgroundColor: '#ffffff' })
    return canvas.toDataURL('image/png')
  } finally {
    wrapper.remove()
  }
}

/**
 * Charts in this app come in three shapes: a Chart.js <canvas> (Índice de
 * Cesta), a hand-rolled <svg> (Variação de Preços), or plain div/CSS bars
 * (Ruptura Digital) — this picks whichever the container actually holds so
 * callers don't need to know which kind it is. Exported separately from
 * exportChartContainerAsPng (which triggers a download) so the standalone
 * HTML exporter can bake the same PNG in as a static `<a download>` link —
 * offline, no JS needed to generate it on tap.
 */
export async function getChartContainerPngDataUrl(container: HTMLElement): Promise<string> {
  const canvas = container.querySelector('canvas')
  if (canvas) {
    return canvas.toDataURL('image/png')
  }

  const svg = container.querySelector('svg')
  if (svg) {
    return svgToPngDataUrl(svg)
  }

  return domElementToPngDataUrl(container)
}

export async function exportChartContainerAsPng(
  container: HTMLElement,
  filename: string,
): Promise<void> {
  downloadDataUrl(await getChartContainerPngDataUrl(container), filename)
}
